import React, { useState, useEffect } from 'react';
import { 
  Target, Calendar, GitBranch, BarChart3, 
  TrendingUp, AlertTriangle, CheckCircle, Clock,
  FileText, Users, Lightbulb, Award, Eye, Plus, Upload
} from 'lucide-react';
import SEOAnalysisPanel from '../components/SEOAnalysisPanel';
import ScheduledPublishingPanel from '../components/ScheduledPublishingPanel';
import ContentVersioningPanel from '../components/ContentVersioningPanel';
import EditorialCalendar from '../components/EditorialCalendar';
import CMSAuth from '../components/CMSAuth';
import { useAuth } from '../contexts/AuthContext';
import { cmsFetch } from '../lib/cmsApi';

const AdvancedCMSDashboard = () => {
  const { user: authUser, isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [insights, setInsights] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedWalk, setSelectedWalk] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      setUser({
        name: authUser?.displayName?.split(' ')[0] || authUser?.email?.split('@')[0] || 'User',
        role: 'admin',
      });
      fetchDashboardData();
    }
  }, [hasAccess]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchAnalytics(),
        fetchInsights(),
        fetchNotifications()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await cmsFetch('/api/cms/analytics/overview');

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await cmsFetch('/api/cms/insights');

      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Error fetching insights:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await cmsFetch('/api/cms/notifications?unread_only=true&limit=5');

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'seo': return Target;
      case 'performance': return TrendingUp;
      case 'content_gap': return Lightbulb;
      default: return BarChart3;
    }
  };

  const getInsightColor = (trend) => {
    switch (trend) {
      case 'attention_needed': return 'text-red-600 bg-red-100';
      case 'opportunity': return 'text-blue-600 bg-blue-100';
      case 'stable': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const InsightCard = ({ insight }) => {
    const Icon = getInsightIcon(insight.type);
    const colorClasses = getInsightColor(insight.trend);

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-start">
          <div className={`p-2 rounded-lg ${colorClasses} mr-3`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-900">{insight.title}</h4>
            <p className="text-sm text-gray-600 mt-1">{insight.description}</p>
            <p className="text-xs text-blue-600 mt-2">{insight.recommendation}</p>
          </div>
        </div>
      </div>
    );
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue", trend }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`flex-shrink-0 p-3 rounded-lg bg-${color}-100`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-500">{title}</h3>
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {trend && (
          <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            trend === 'up' ? 'text-green-600 bg-green-100' :
            trend === 'down' ? 'text-red-600 bg-red-100' :
            'text-gray-600 bg-gray-100'
          }`}>
            {trend === 'up' && <TrendingUp className="w-3 h-3 mr-1" />}
            {trend}
          </div>
        )}
      </div>
    </div>
  );

  if (!hasAccess) {
    return <CMSAuth onAuthenticated={() => setHasAccess(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Advanced CMS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Advanced CMS Dashboard</h1>
              <p className="text-sm text-gray-500">Professional content management with advanced features</p>
            </div>
            <div className="flex items-center space-x-4">
              {notifications.length > 0 && (
                <div className="relative">
                  <button className="p-2 text-gray-400 hover:text-gray-600">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs text-white">
                      {notifications.length}
                    </div>
                  </button>
                </div>
              )}
              
              
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: BarChart3 },
              { id: 'seo', name: 'SEO Tools', icon: Target },
              { id: 'scheduling', name: 'Scheduling', icon: Calendar },
              { id: 'versioning', name: 'Versioning', icon: GitBranch },
              { id: 'calendar', name: 'Editorial Calendar', icon: Calendar }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                icon={FileText}
                title="Total Content"
                value={analytics?.total_walks || 0}
                subtitle="All walks"
                color="blue"
                trend="stable"
              />
              <StatCard
                icon={Clock}
                title="Pending Review"
                value={analytics?.pending_reviews || 0}
                subtitle="Awaiting approval"
                color="yellow"
                trend="down"
              />
              <StatCard
                icon={CheckCircle}
                title="Published"
                value={analytics?.status_counts?.published || 0}
                subtitle="Live content"
                color="green"
                trend="up"
              />
              <StatCard
                icon={Target}
                title="Avg SEO Score"
                value="75"
                subtitle="Content quality"
                color="purple"
                trend="up"
              />
            </div>

            {/* Content Insights */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Lightbulb className="w-5 h-5 text-yellow-500 mr-2" />
                  Content Insights
                </h3>
                <div className="space-y-4">
                  {insights.length > 0 ? (
                    insights.map((insight, index) => (
                      <InsightCard key={index} insight={insight} />
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No insights available</p>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <Award className="w-5 h-5 text-blue-500 mr-2" />
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  {analytics?.recent_walks?.slice(0, 5).map((walk, index) => (
                    <div key={walk.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">{walk.title}</h4>
                        <p className="text-xs text-gray-500">{new Date(walk.updated_at).toLocaleDateString()}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        walk.status === 'published' ? 'bg-green-100 text-green-800' :
                        walk.status === 'pending_review' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {walk.status.replace('_', ' ')}
                      </span>
                    </div>
                  )) || (
                    <p className="text-gray-500 text-center py-4">No recent activity</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Plus className="w-5 h-5 text-blue-500 mr-2" />
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => window.location.href = '/cms/walks/create'}
                  className="flex flex-col items-center p-4 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <Plus className="w-8 h-8 text-blue-600 mb-2" />
                  <span className="text-sm font-medium text-blue-900">Create New Walk</span>
                  <span className="text-xs text-blue-600">Add fresh content</span>
                </button>
                
                <button
                  onClick={() => window.location.href = '/cms/walks'}
                  className="flex flex-col items-center p-4 border-2 border-dashed border-green-300 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors"
                >
                  <FileText className="w-8 h-8 text-green-600 mb-2" />
                  <span className="text-sm font-medium text-green-900">Manage Walks</span>
                  <span className="text-xs text-green-600">Edit existing content</span>
                </button>
                
                <button
                  onClick={() => window.location.href = '/cms/media'}
                  className="flex flex-col items-center p-4 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                >
                  <Upload className="w-8 h-8 text-purple-600 mb-2" />
                  <span className="text-sm font-medium text-purple-900">Upload Media</span>
                  <span className="text-xs text-purple-600">Add images & files</span>
                </button>
                
                <button
                  onClick={() => window.location.href = '/cms/users'}
                  className="flex flex-col items-center p-4 border-2 border-dashed border-orange-300 rounded-lg hover:border-orange-400 hover:bg-orange-50 transition-colors"
                >
                  <Users className="w-8 h-8 text-orange-600 mb-2" />
                  <span className="text-sm font-medium text-orange-900">Manage Users</span>
                  <span className="text-xs text-orange-600">User roles & permissions</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'seo' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Select Walk for SEO Analysis</h4>
                <div className="space-y-2">
                  {analytics?.recent_walks?.slice(0, 5).map((walk) => (
                    <button
                      key={walk.id}
                      onClick={() => setSelectedWalk(walk)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedWalk?.id === walk.id
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{walk.title}</p>
                      <p className="text-xs text-gray-500">{walk.region}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <SEOAnalysisPanel walkId={selectedWalk?.id} />
            </div>
          </div>
        )}

        {activeTab === 'scheduling' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Select Walk for Scheduling</h4>
                <div className="space-y-2">
                  {analytics?.recent_walks?.slice(0, 5).map((walk) => (
                    <button
                      key={walk.id}
                      onClick={() => setSelectedWalk(walk)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedWalk?.id === walk.id
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{walk.title}</p>
                      <p className="text-xs text-gray-500">{walk.region}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <ScheduledPublishingPanel walkId={selectedWalk?.id} walkTitle={selectedWalk?.title} />
            </div>
          </div>
        )}

        {activeTab === 'versioning' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Select Walk for Version History</h4>
                <div className="space-y-2">
                  {analytics?.recent_walks?.slice(0, 5).map((walk) => (
                    <button
                      key={walk.id}
                      onClick={() => setSelectedWalk(walk)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedWalk?.id === walk.id
                          ? 'border-blue-300 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">{walk.title}</p>
                      <p className="text-xs text-gray-500">{walk.region}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <ContentVersioningPanel 
                walkId={selectedWalk?.id} 
                walkTitle={selectedWalk?.title}
                onVersionRestore={() => {
                  // Refresh analytics after version restore
                  fetchAnalytics();
                }}
              />
            </div>
          </div>
        )}

        {activeTab === 'calendar' && (
          <EditorialCalendar />
        )}
      </div>
    </div>
  );
};

export default AdvancedCMSDashboard;